import { createContext, useCallback, useContext, useMemo, useState } from 'react'

interface StagedContentGuardContextValue {
  // True while any registered section, anywhere in the routed content, has
  // staged/unsaved content.
  hasStagedContent: boolean
  // A section reports its own staged/unsaved state; keyed so sections don't
  // clobber each other.
  reportStagedContent: (key: string, value: boolean) => void
}

const StagedContentGuardContext =
  createContext<StagedContentGuardContextValue | null>(null)

/**
 * Shared across the app shell (nav, org selector) and the routed pages
 * beneath it - the org selector lives outside the pages that actually hold
 * staged-upload/unsaved-changes state, so switching orgs needs its own way
 * to ask "is anything staged right now" before it fires. Pages report their
 * own local state in; this only aggregates it, callers decide what
 * "confirmed, proceed" means.
 */
export function StagedContentGuardProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const [sections, setSections] = useState<Record<string, boolean>>({})

  const reportStagedContent = useCallback((key: string, value: boolean) => {
    setSections((prev) =>
      prev[key] === value ? prev : { ...prev, [key]: value },
    )
  }, [])

  const hasStagedContent = useMemo(
    () => Object.values(sections).some(Boolean),
    [sections],
  )

  const value = useMemo(
    () => ({ hasStagedContent, reportStagedContent }),
    [hasStagedContent, reportStagedContent],
  )

  return (
    <StagedContentGuardContext.Provider value={value}>
      {children}
    </StagedContentGuardContext.Provider>
  )
}

export function useStagedContentGuard(): StagedContentGuardContextValue {
  const ctx = useContext(StagedContentGuardContext)
  if (!ctx) {
    throw new Error(
      'useStagedContentGuard must be used within a StagedContentGuardProvider',
    )
  }
  return ctx
}
